import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiLogOut,
  FiLoader,
  FiAlertTriangle,
  FiUpload,
  FiLock,
  FiUser,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiPackage,
  FiImage,
} from "react-icons/fi";

const API_URL =
  import.meta.env?.VITE_API_URL || "https://monacoaberturas.com";

const TOKEN_KEY = "monaco_admin_token";

/* Altura real del navbar fijo del sitio público.
   Ajustá este valor si tu navbar cambia de tamaño. */
const NAVBAR_HEIGHT = 96;

/* ───────────────── HELPERS DE API ───────────────── */

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    clearToken();
    throw new Error("SESION_EXPIRADA");
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || "Ocurrió un error inesperado.");
  }

  return data;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const easeOut = [0.22, 1, 0.36, 1];

/* Bloquea el scroll del body mientras `activo` sea true (usado por los modales) */
function useBloquearScroll(activo) {
  useEffect(() => {
    if (!activo) return;
    const overflowPrevio = document.body.style.overflow;
    const paddingPrevio = document.body.style.paddingRight;

    // Evita el "salto" de layout al ocultar la scrollbar del body
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = overflowPrevio;
      document.body.style.paddingRight = paddingPrevio;
    };
  }, [activo]);
}

/* ───────────────── LOGIN ───────────────── */

function LoginScreen({ onLoginExitoso }) {
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/login.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Usuario o contraseña incorrectos.");
      }
      setToken(data.token);
      onLoginExitoso();
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center bg-[#0f0d0b] px-5"
      style={{ paddingTop: NAVBAR_HEIGHT }}
    >
      <div
        className="
          pointer-events-none absolute inset-0
          bg-[linear-gradient(rgba(201,151,58,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(201,151,58,0.04)_1px,transparent_1px)]
          bg-[size:60px_60px]
        "
      />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#c9973a]/10 blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: easeOut }}
        className="relative z-10 w-full max-w-md rounded-sm border border-[#c9973a]/15 bg-white/[0.03] p-10 shadow-2xl shadow-black/40 backdrop-blur-xl"
      >
        <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#c9973a] to-transparent" />

        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4, ease: easeOut }}
            className="flex h-14 w-14 items-center justify-center rounded-full border border-[#c9973a]/30 bg-gradient-to-br from-[#c9973a]/20 to-[#8b3520]/20 text-2xl text-[#f0c96b]"
          >
            <FiLock />
          </motion.div>
          <h1
            className="text-[2rem] text-[#f9f5ef]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Panel de Productos
          </h1>
          <p
            className="text-[0.85rem] text-[#8f8678]"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            Monaco Aberturas — acceso restringido
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div>
            <label
              className="mb-2 flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              <FiUser className="text-[#c9973a]" /> Usuario
            </label>
            <input
              type="text"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              required
              autoFocus
              className="w-full rounded-sm border border-[#c9973a]/20 bg-[#1a1814] px-4 py-3 text-[#f9f5ef] outline-none transition-colors duration-200 focus:border-[#c9973a]/60"
              style={{ fontFamily: "'Jost', sans-serif" }}
            />
          </div>

          <div>
            <label
              className="mb-2 flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              <FiLock className="text-[#c9973a]" /> Contraseña
            </label>
            <div className="relative">
              <input
                type={mostrarPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-sm border border-[#c9973a]/20 bg-[#1a1814] px-4 py-3 pr-12 text-[#f9f5ef] outline-none transition-colors duration-200 focus:border-[#c9973a]/60"
                style={{ fontFamily: "'Jost', sans-serif" }}
              />
              <button
                type="button"
                onClick={() => setMostrarPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8f8678] transition-colors hover:text-[#c9973a]"
              >
                {mostrarPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-start gap-2 overflow-hidden rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-3 text-[0.85rem] text-red-300"
              >
                <FiAlertTriangle className="mt-0.5 flex-shrink-0" />
                <span style={{ fontFamily: "'Jost', sans-serif" }}>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            disabled={cargando}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="mt-2 flex items-center justify-center gap-2 rounded-sm bg-gradient-to-br from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-6 py-3 text-[0.8rem] font-semibold uppercase tracking-[0.25em] text-[#1a1814] shadow-lg shadow-[#c9973a]/10 transition-shadow hover:shadow-[#c9973a]/20 disabled:opacity-60"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            {cargando ? <FiLoader className="animate-spin" /> : "Ingresar"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}

/* ───────────────── FORM MODAL (crear / editar) ───────────────── */

function ProductoFormModal({ producto, onClose, onGuardado }) {
  const esEdicion = Boolean(producto?.id);

  useBloquearScroll(true);

  const [form, setForm] = useState({
    titulo: producto?.titulo || "",
    descripcion: producto?.descripcion || "",
    badge: producto?.badge || "Disponible",
    icono: producto?.icono || "FiLayers",
    orden: producto?.orden ?? 0,
    activo: producto?.activo ?? 1,
  });
  const [imagenPreview, setImagenPreview] = useState(
    producto?.imagen_base64 || null
  );
  const [imagenNueva, setImagenNueva] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);

  function actualizarCampo(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  async function procesarArchivo(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("El archivo debe ser una imagen.");
      return;
    }
    if (file.size > 3.5 * 1024 * 1024) {
      setError("La imagen pesa demasiado. Usá una de hasta 3.5MB.");
      return;
    }
    setError(null);
    const base64 = await fileToBase64(file);
    setImagenPreview(base64);
    setImagenNueva(base64);
  }

  async function handleImagenChange(e) {
    const file = e.target.files?.[0];
    await procesarArchivo(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    setArrastrando(false);
    const file = e.dataTransfer.files?.[0];
    procesarArchivo(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!esEdicion && !imagenNueva) {
      setError("La foto del producto es obligatoria.");
      return;
    }

    setGuardando(true);
    try {
      const body = {
        ...form,
        orden: Number(form.orden),
        activo: Number(form.activo),
        ...(imagenNueva ? { imagen_base64: imagenNueva } : {}),
      };

      const data = esEdicion
        ? await apiFetch(`/productos.php?id=${producto.id}`, {
            method: "PUT",
            body: JSON.stringify(body),
          })
        : await apiFetch(`/productos.php`, {
            method: "POST",
            body: JSON.stringify(body),
          });

      onGuardado(data);
    } catch (err) {
      setError(
        err.message === "SESION_EXPIRADA"
          ? "Tu sesión expiró. Volvé a iniciar sesión."
          : err.message
      );
    } finally {
      setGuardando(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 px-4 py-8 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ duration: 0.3, ease: easeOut }}
        onClick={(e) => e.stopPropagation()}
        className="modal-scroll relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-sm border border-[#c9973a]/20 bg-[#13110e] p-8 shadow-2xl shadow-black/60"
      >
        <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#c9973a] to-transparent" />

        <style>{`
          .modal-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(201,151,58,0.45) transparent;
          }
          .modal-scroll::-webkit-scrollbar {
            width: 8px;
          }
          .modal-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .modal-scroll::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, rgba(240,201,107,0.6), rgba(201,151,58,0.45));
            border-radius: 999px;
            border: 2px solid #13110e;
          }
          .modal-scroll::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, rgba(240,201,107,0.85), rgba(201,151,58,0.7));
          }
        `}</style>

        <div className="mb-6 flex items-center justify-between">
          <h2
            className="text-[1.8rem] text-[#f9f5ef]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {esEdicion ? "Editar producto" : "Nuevo producto"}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-[#8f8678] transition-colors hover:bg-white/5 hover:text-[#c9973a]"
          >
            <FiX size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Imagen */}
          <div>
            <label
              className="mb-2 block text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Foto del producto
            </label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setArrastrando(true);
              }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={handleDrop}
              className={`flex items-center gap-4 rounded-sm border border-dashed p-3 transition-colors ${
                arrastrando
                  ? "border-[#c9973a]/70 bg-[#c9973a]/5"
                  : "border-[#c9973a]/20"
              }`}
            >
              <div className="flex h-24 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-sm border border-[#c9973a]/20 bg-[#0a0908]">
                <AnimatePresence mode="wait">
                  {imagenPreview ? (
                    <motion.img
                      key={imagenPreview}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      src={imagenPreview}
                      alt="Vista previa"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      key="placeholder"
                    >
                      <FiImage className="text-2xl text-[#8f8678]" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-sm border border-[#c9973a]/30 px-5 py-2.5 text-[0.75rem] uppercase tracking-[0.2em] text-[#c9973a] transition-colors hover:bg-[#c9973a]/10">
                  <FiUpload size={13} />
                  Elegir imagen
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/webp"
                    onChange={handleImagenChange}
                    className="hidden"
                  />
                </label>
                <span
                  className="text-[0.7rem] text-[#8f8678]"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  o arrastrá una imagen acá · máx. 3.5MB
                </span>
              </div>
            </div>
          </div>

          {/* Orden + Visible */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className="mb-2 block text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                Orden
              </label>
              <input
                type="number"
                value={form.orden}
                onChange={(e) => actualizarCampo("orden", e.target.value)}
                className="w-full rounded-sm border border-[#c9973a]/20 bg-[#1a1814] px-3 py-2.5 text-[#f9f5ef] outline-none transition-colors focus:border-[#c9973a]/60"
                style={{ fontFamily: "'Jost', sans-serif" }}
              />
            </div>
            <div>
              <label
                className="mb-2 block text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                Visible
              </label>
              <select
                value={form.activo}
                onChange={(e) => actualizarCampo("activo", e.target.value)}
                className="w-full rounded-sm border border-[#c9973a]/20 bg-[#1a1814] px-3 py-2.5 text-[#f9f5ef] outline-none transition-colors focus:border-[#c9973a]/60"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                <option value={1}>Sí</option>
                <option value={0}>No</option>
              </select>
            </div>
          </div>

          {/* Titulo */}
          <div>
            <label
              className="mb-2 block text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Título
            </label>
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => actualizarCampo("titulo", e.target.value)}
              required
              className="w-full rounded-sm border border-[#c9973a]/20 bg-[#1a1814] px-4 py-2.5 text-[#f9f5ef] outline-none transition-colors focus:border-[#c9973a]/60"
              style={{ fontFamily: "'Jost', sans-serif" }}
            />
          </div>

          {/* Descripcion */}
          <div>
            <label
              className="mb-2 block text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Descripción
            </label>
            <textarea
              value={form.descripcion}
              onChange={(e) => actualizarCampo("descripcion", e.target.value)}
              required
              rows={3}
              className="w-full resize-none rounded-sm border border-[#c9973a]/20 bg-[#1a1814] px-4 py-2.5 text-[#f9f5ef] outline-none transition-colors focus:border-[#c9973a]/60"
              style={{ fontFamily: "'Jost', sans-serif" }}
            />
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-start gap-2 overflow-hidden rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-3 text-[0.85rem] text-red-300"
              >
                <FiAlertTriangle className="mt-0.5 flex-shrink-0" />
                <span style={{ fontFamily: "'Jost', sans-serif" }}>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm border border-[#8f8678]/30 px-6 py-2.5 text-[0.75rem] uppercase tracking-[0.2em] text-[#8f8678] transition-colors hover:bg-white/5"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Cancelar
            </button>
            <motion.button
              type="submit"
              disabled={guardando}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 rounded-sm bg-gradient-to-br from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-6 py-2.5 text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-[#1a1814] shadow-lg shadow-[#c9973a]/10 disabled:opacity-60"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {guardando && <FiLoader className="animate-spin" />}
              {esEdicion ? "Guardar cambios" : "Crear producto"}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

/* ───────────────── CONFIRMACIÓN DE BORRADO ───────────────── */

function ConfirmarBorrado({ producto, onClose, onConfirmar, borrando }) {
  useBloquearScroll(true);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.25, ease: easeOut }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-sm border border-[#c9973a]/20 bg-[#13110e] p-8 text-center shadow-2xl shadow-black/60"
      >
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 text-red-400"
        >
          <FiAlertTriangle className="text-xl" />
        </motion.div>
        <h3
          className="mb-2 text-[1.4rem] text-[#f9f5ef]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          ¿Eliminar este producto?
        </h3>
        <p
          className="mb-6 text-[0.9rem] text-[#b7aea2]"
          style={{ fontFamily: "'Jost', sans-serif" }}
        >
          "{producto.titulo}" se va a eliminar permanentemente. Esta acción no
          se puede deshacer.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={onClose}
            className="rounded-sm border border-[#8f8678]/30 px-6 py-2.5 text-[0.75rem] uppercase tracking-[0.2em] text-[#8f8678] transition-colors hover:bg-white/5"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            Cancelar
          </button>
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            onClick={onConfirmar}
            disabled={borrando}
            className="flex items-center gap-2 rounded-sm bg-red-600/80 px-6 py-2.5 text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-red-600 disabled:opacity-60"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            {borrando && <FiLoader className="animate-spin" />}
            Eliminar
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ───────────────── TOAST ───────────────── */

function Toast({ mensaje, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.3, ease: easeOut }}
      className="fixed bottom-6 right-6 z-[1200] flex items-center gap-3 rounded-sm border border-[#c9973a]/30 bg-[#1a1814] px-5 py-4 shadow-2xl shadow-black/50"
    >
      <FiCheckCircle className="text-[#c9973a]" />
      <span
        className="text-[0.85rem] text-[#f9f5ef]"
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        {mensaje}
      </span>
    </motion.div>
  );
}

/* ───────────────── FILA DE PRODUCTO ───────────────── */

function FilaProducto({ producto, index, onEditar, onBorrar }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: easeOut }}
      whileHover={{ backgroundColor: "rgba(255,255,255,0.025)" }}
      className="flex flex-col gap-4 border-b border-[#c9973a]/10 px-4 py-4 sm:flex-row sm:items-center"
    >
      <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-sm border border-[#c9973a]/15 bg-[#0a0908]">
        {producto.imagen_base64 ? (
          <img
            src={producto.imagen_base64}
            alt={producto.titulo}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[#8f8678]">
            <FiImage />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p
          className="truncate text-[1rem] text-[#f9f5ef]"
          style={{ fontFamily: "'Jost', sans-serif", fontWeight: 500 }}
        >
          {producto.titulo}
        </p>
        <p
          className="truncate text-[0.8rem] text-[#8f8678]"
          style={{ fontFamily: "'Jost', sans-serif" }}
        >
          Orden {producto.orden}
        </p>
      </div>

      <span
        className={`w-fit flex-shrink-0 rounded-sm px-3 py-1 text-[0.65rem] uppercase tracking-[0.2em] ${
          Number(producto.activo) === 1
            ? "border border-[#c9973a]/30 text-[#c9973a]"
            : "border border-[#8f8678]/30 text-[#8f8678]"
        }`}
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        {Number(producto.activo) === 1 ? "Visible" : "Oculto"}
      </span>

      <div className="flex flex-shrink-0 gap-2 self-end sm:self-auto">
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onEditar(producto)}
          className="flex h-9 w-9 items-center justify-center rounded-sm border border-[#c9973a]/20 text-[#c9973a] transition-colors hover:bg-[#c9973a]/10"
          title="Editar"
        >
          <FiEdit2 size={15} />
        </motion.button>
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onBorrar(producto)}
          className="flex h-9 w-9 items-center justify-center rounded-sm border border-red-500/20 text-red-400 transition-colors hover:bg-red-500/10"
          title="Eliminar"
        >
          <FiTrash2 size={15} />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ───────────────── PANEL PRINCIPAL ───────────────── */

function PanelAdmin({ onLogout }) {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [modalForm, setModalForm] = useState(null);
  const [productoABorrar, setProductoABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [toast, setToast] = useState(null);

  const cargarProductos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await apiFetch("/productos.php?todos=1");
      setProductos(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.message === "SESION_EXPIRADA") {
        onLogout();
        return;
      }
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }, [onLogout]);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  function handleGuardado(productoGuardado) {
    const esEdicion = Boolean(modalForm?.id);
    setModalForm(null);
    setToast(
      esEdicion
        ? "Producto actualizado correctamente."
        : "Producto creado correctamente."
    );
    cargarProductos();
  }

  async function confirmarBorrado() {
    if (!productoABorrar) return;
    setBorrando(true);
    try {
      await apiFetch(`/productos.php?id=${productoABorrar.id}`, {
        method: "DELETE",
      });
      setProductoABorrar(null);
      setToast("Producto eliminado.");
      cargarProductos();
    } catch (err) {
      if (err.message === "SESION_EXPIRADA") {
        onLogout();
        return;
      }
      setError(err.message);
    } finally {
      setBorrando(false);
    }
  }

  function handleLogoutClick() {
    clearToken();
    onLogout();
  }

  return (
    <div
      className="relative min-h-screen bg-[#0f0d0b] text-white"
      style={{ paddingTop: NAVBAR_HEIGHT }}
    >
      <div
        className="
          pointer-events-none fixed inset-0 -z-10
          bg-[linear-gradient(rgba(201,151,58,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(201,151,58,0.04)_1px,transparent_1px)]
          bg-[size:60px_60px]
        "
      />

      {/* Sub-header del panel, fluye normalmente con la página (no fixed/sticky) */}
      <motion.header
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: easeOut }}
        className="border-b border-[#c9973a]/10 bg-[#0f0d0b]/90 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-[#c9973a]/30 bg-gradient-to-br from-[#c9973a]/20 to-[#8b3520]/20 text-[#f0c96b]">
              <FiPackage size={18} />
            </div>
            <div>
              <h1
                className="text-[1.5rem] leading-tight text-[#f9f5ef]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Panel de Productos
              </h1>
              <p
                className="text-[0.7rem] uppercase tracking-[0.25em] text-[#8f8678]"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                Monaco Aberturas
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleLogoutClick}
            className="flex w-fit items-center gap-2 rounded-sm border border-[#c9973a]/20 px-5 py-2.5 text-[0.75rem] uppercase tracking-[0.2em] text-[#c9973a] transition-colors hover:bg-[#c9973a]/10"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            <FiLogOut size={14} /> Salir
          </motion.button>
        </div>
      </motion.header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: easeOut }}
          className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <p
            className="text-[0.85rem] text-[#b7aea2]"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            {productos.length}{" "}
            {productos.length === 1 ? "producto" : "productos"} en total
          </p>
          <motion.button
            onClick={() => setModalForm({})}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex w-fit items-center gap-2 rounded-sm bg-gradient-to-br from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-6 py-2.5 text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-[#1a1814] shadow-lg shadow-[#c9973a]/10 transition-shadow hover:shadow-[#c9973a]/20"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            <FiPlus /> Nuevo producto
          </motion.button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15, ease: easeOut }}
          className="rounded-sm border border-[#c9973a]/10 bg-white/[0.02] shadow-xl shadow-black/20"
        >
          <AnimatePresence mode="wait">
            {cargando && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center gap-3 py-16 text-[#8f8678]"
              >
                <FiLoader className="animate-spin text-2xl text-[#c9973a]" />
                <span style={{ fontFamily: "'Jost', sans-serif" }}>
                  Cargando productos…
                </span>
              </motion.div>
            )}

            {!cargando && error && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center gap-3 py-16 text-center text-[#8f8678]"
              >
                <FiAlertTriangle className="text-2xl text-[#c9973a]" />
                <p style={{ fontFamily: "'Jost', sans-serif" }}>{error}</p>
                <button
                  onClick={cargarProductos}
                  className="rounded-sm border border-[#c9973a]/30 px-5 py-2 text-[0.75rem] uppercase tracking-[0.2em] text-[#c9973a] transition-colors hover:bg-[#c9973a]/10"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  Reintentar
                </button>
              </motion.div>
            )}

            {!cargando && !error && productos.length === 0 && (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center gap-2 py-16 text-center text-[#8f8678]"
              >
                <FiPackage className="mb-1 text-2xl text-[#c9973a]" />
                <p style={{ fontFamily: "'Jost', sans-serif" }}>
                  Todavía no hay productos cargados.
                </p>
              </motion.div>
            )}

            {!cargando && !error && productos.length > 0 && (
              <motion.div key="lista" layout>
                <AnimatePresence>
                  {productos.map((producto, index) => (
                    <FilaProducto
                      key={producto.id}
                      producto={producto}
                      index={index}
                      onEditar={setModalForm}
                      onBorrar={setProductoABorrar}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      <AnimatePresence>
        {modalForm !== null && (
          <ProductoFormModal
            producto={modalForm}
            onClose={() => setModalForm(null)}
            onGuardado={handleGuardado}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {productoABorrar && (
          <ConfirmarBorrado
            producto={productoABorrar}
            borrando={borrando}
            onClose={() => setProductoABorrar(null)}
            onConfirmar={confirmarBorrado}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && <Toast mensaje={toast} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
}

/* ───────────────── ROOT: decide login vs panel ───────────────── */

export default function AdminProductos() {
  const [logueado, setLogueado] = useState(Boolean(getToken()));

  if (!logueado) {
    return <LoginScreen onLoginExitoso={() => setLogueado(true)} />;
  }

  return <PanelAdmin onLogout={() => setLogueado(false)} />;
}